import { batch as reduxBatch } from 'react-redux'
import { identity, difference } from 'lodash'

import { emptyFile } from '../store/newFileState'
import selectors from '../selectors'
import {
  UNDO,
  REDO,
  FORCED_BATCH_END,
  FORCED_BATCH_START,
  FILE_LOADED,
  DISMISS_RECENT_CHANGE_FLAG,
  UNDO_N_TIMES,
  REDO_N_TIMES,
} from '../constants/ActionTypes'

const {
  lastUndoStateSelector,
  lastRedoStateSelector,
  fullFileStateSelector,
  undoRedoChangeIdSelector,
  undoHistorySelector,
  undoFutureSelector,
} = selectors(identity)

/**
 * @typedef {import('../index').PlottrFile} PlottrFile
 * @typedef {import('../index').Dispatch} Dispatch
 * @typedef {import('../index').GetState} GetState
 * @typedef {import('../index').PlottrState} PlottrState
 */

/**
 * @typedef {ReturnType<typeof _undo> | ReturnType<typeof _redo> | ReturnType<typeof _undoNTimes> | ReturnType<typeof _redoNTimes> | ReturnType<typeof dismissUndoRedoDialog> | ReturnType<typeof _forcedBatchStart> | ReturnType<typeof _forcedBatchEnd>} UndoActions
 */

/**
 * @param {PlottrFile} userState
 * @return {boolean}
 */
const hasAllKeys = (userState) => {
  const emptyFileState = emptyFile('DummyFile', '2022.11.2')
  return difference(Object.keys(emptyFileState), Object.keys(userState)).length === 0
}

/**
 * @param {String} oldType
 * @param {String} oldLabel
 * @param {PlottrFile} state
 * @param {number} id
 * @return {{ type: 'UNDO', oldType: String, oldLabel: String, state: PlottrFile, id: number }}
 */
export const _undo = (oldType, oldLabel, state, id) => {
  return {
    type: UNDO,
    oldType,
    oldLabel,
    state,
    id,
  }
}

/**
 * @return {function(Dispatch, GetState): Void}
 */
export const undo = () => (dispatch, getState) => {
  const undoState = lastUndoStateSelector(getState())
  if (
    undoState &&
    typeof undoState === 'object' &&
    hasAllKeys(undoState.state) &&
    undoState.actionType !== FILE_LOADED
  ) {
    const id = Math.floor(Math.random() * 1000000)
    dispatch(_undo(undoState.actionType, undoState.actionLabel, undoState.state, id))
    setTimeout(() => {
      if (undoRedoChangeIdSelector(getState()) === id) {
        dispatch(dismissUndoRedoDialog())
      }
    }, 3000)
  }
}

/**
 * @param {String} oldType
 * @param {String} oldLabel
 * @param {PlottrFile} state
 * @param {number} id
 * @return {{ type: 'REDO', oldType: String, oldLabel: String, state: PlottrFile, id: number }}
 */
export const _redo = (oldType, oldLabel, state, id) => {
  return {
    type: REDO,
    oldType,
    oldLabel,
    state,
    id,
  }
}

/**
 * @return {function(Dispatch, GetState): Void}
 */
export const redo = () => (dispatch, getState) => {
  const redoState = lastRedoStateSelector(getState())
  if (
    redoState &&
    typeof redoState === 'object' &&
    hasAllKeys(redoState.state) &&
    redoState.actionType !== FILE_LOADED
  ) {
    const id = Math.floor(Math.random() * 1000000)
    dispatch(_redo(redoState.actionType, redoState.actionLabel, redoState.state, id))
    setTimeout(() => {
      if (undoRedoChangeIdSelector(getState()) === id) {
        dispatch(dismissUndoRedoDialog())
      }
    }, 3000)
  }
}

/**
 * @param {number} n
 * @param {String} oldType
 * @param {String} oldLabel
 * @param {PlottrFile} state
 * @param {number} id
 * @return {{ type: 'UNDO_N_TIMES', n: number, oldType: String, oldLabel: String, state: PlottrFile, id: number }}
 */
export const _undoNTimes = (n, oldType, oldLabel, state, id) => {
  return {
    type: UNDO_N_TIMES,
    n,
    oldType,
    oldLabel,
    state,
    id,
  }
}

/**
 * @param {number} n
 * @return {function(Dispatch, GetState): Void}
 */
export const undoNTimes = (n) => (dispatch, getState) => {
  const undoHistory = undoHistorySelector(getState())
  if (Array.isArray(undoHistory) && undoHistory.length >= n) {
    const undoState = undoHistory[n]
    if (
      undoState &&
      typeof undoState === 'object' &&
      hasAllKeys(undoState.state) &&
      undoState.actionType !== FILE_LOADED
    ) {
      const id = Math.floor(Math.random() * 1000000)
      dispatch(_undoNTimes(n, undoState.actionType, undoState.actionLabel, undoState.state, id))
      setTimeout(() => {
        if (undoRedoChangeIdSelector(getState()) === id) {
          dispatch(dismissUndoRedoDialog())
        }
      }, 3000)
    }
  }
}

/**
 * @param {number} n
 * @param {String} oldType
 * @param {String} oldLabel
 * @param {PlottrFile} state
 * @param {number} id
 * @return {{ type: 'REDO_N_TIMES', n: number, oldType: String, oldLabel: String, state: PlottrFile, id: number }}
 */
export const _redoNTimes = (n, oldType, oldLabel, state, id) => {
  return {
    type: REDO_N_TIMES,
    n,
    oldType,
    oldLabel,
    state,
    id,
  }
}

/**
 * @param {number} n
 * @return {function(Dispatch, GetState): Void}
 */
export const redoNTimes = (n) => (dispatch, getState) => {
  const undoFuture = undoFutureSelector(getState())
  if (Array.isArray(undoFuture) && undoFuture.length >= n) {
    const redoState = undoFuture[n]
    if (
      redoState &&
      typeof redoState === 'object' &&
      hasAllKeys(redoState.state) &&
      redoState.actionType !== FILE_LOADED
    ) {
      const id = Math.floor(Math.random() * 1000000)
      dispatch(_redoNTimes(n, redoState.actionType, redoState.actionLabel, redoState.state, id))
      setTimeout(() => {
        if (undoRedoChangeIdSelector(getState()) === id) {
          dispatch(dismissUndoRedoDialog())
        }
      }, 3000)
    }
  }
}

/**
 * @return {{ type: 'DISMISS_RECENT_CHANGE_FLAG' }}
 */
export const dismissUndoRedoDialog = () => {
  return {
    type: DISMISS_RECENT_CHANGE_FLAG,
  }
}

/**
 * @param {String} name
 * @return {{ type: 'FORCED_BATCH_START', name: String }}
 */
export const _forcedBatchStart = (name) => {
  return { type: FORCED_BATCH_START, name }
}

/**
 * @param {String} name
 * @return {{ type: 'FORCED_BATCH_END', name: String }}
 */
export const _forcedBatchEnd = (name) => {
  return { type: FORCED_BATCH_END, name }
}

/**
 * @param {String} name
 * @param {function(): Void} actionsThunk
 * @return {function(Dispatch): Void}
 */
export const batch = (name, actionsThunk) => (dispatch) => {
  reduxBatch(() => {
    dispatch(_forcedBatchStart(name))
    actionsThunk()
    dispatch(_forcedBatchEnd(name))
  })
}

// N.B. These two actions (forceBatchStart and forceBatchEnd) are
// usually called from odd places; for example, they're called from
// the root reducer.  In those cases, the middleware wont run and the
// action will lack a shadow state.  We add the shadow state here to
// enable undo/redo to work as expected.

/**
 * @param {String} name
 * @param {PlottrState} rawFullFileState
 * @return {{ type: FORCED_BATCH_START, _shadow: PlottrFile, name: String }}
 */
export const forceBatchStart = (name, rawFullFileState) => {
  const currentUserFileState = fullFileStateSelector(rawFullFileState)
  return { type: FORCED_BATCH_START, _shadow: currentUserFileState, name }
}

/**
 * @param {String} name
 * @param {PlottrState} rawFullFileState
 * @return {{ type: FORCED_BATCH_END, name: String, _shadow: PlottrFile }}
 */
export const forceBatchEnd = (name, rawFullFileState) => {
  const currentUserFileState = fullFileStateSelector(rawFullFileState)
  return { type: FORCED_BATCH_END, name, _shadow: currentUserFileState }
}
